import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import { api } from './utils/api';
import ErrorBoundary from './components/layout/ErrorBoundary';
import { Button } from './components/ui/button';
import { Spinner } from './components/ui/spinner';
import ProtectedLayout from './components/auth/ProtectedLayout';

// Lazy-load marketing pages — only fetched when their route is visited
const Features = lazy(() => import('./components/public/Features'));
const Pricing = lazy(() => import('./components/public/Pricing'));
const About = lazy(() => import('./components/public/About'));
const Contact = lazy(() => import('./components/public/Contact'));
const ApiDocs = lazy(() => import('./components/public/ApiDocs'));
const Integrations = lazy(() => import('./components/public/Integrations'));
const Careers = lazy(() => import('./components/public/Careers'));
const PrivacyPolicy = lazy(() => import('./components/public/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/public/TermsOfService'));
const CookiePolicy = lazy(() => import('./components/public/CookiePolicy'));

// Lazy-load auth pages — only fetched when the user reaches them
const SignIn = lazy(() => import('./components/auth/SignIn'));
const SignUp = lazy(() => import('./components/auth/SignUp'));

// Lazy-load route-level components to reduce initial bundle size
const AccountDetail = lazy(() => import('./components/accounts/AccountDetail'));
const Settings = lazy(() => import('./components/settings/Settings'));
const MyIntegrations = lazy(() => import('./components/integrations/MyIntegrations'));
const BusinessProfile = lazy(() => import('./components/settings/business-profile/BusinessProfile'));
const Onboarding = lazy(() => import('./components/onboarding/Onboarding'));
const OnboardingInsights = lazy(() => import('./components/onboarding/OnboardingInsights'));
const AIActions = lazy(() => import('./components/workflows/AIActions'));
const Accounts = lazy(() => import('./components/accounts/Accounts'));
const Analytics = lazy(() => import('./components/analytics/Analytics'));
const TasksPage = lazy(() => import('./components/tasks/TasksPage'));
const MeetingsPage = lazy(() => import('./components/meetings/MeetingsPage'));
const OpportunitiesPage = lazy(() => import('./components/opportunities/OpportunitiesPage'));
const Communications = lazy(() => import('./components/communications/Communications'));
const CalebChat = lazy(() => import('./components/caleb/CalebChat'));
const KnowledgeBase = lazy(() => import('./components/knowledge/KnowledgeBase'));

// P11 fix: Lazy-load heavy admin pages (only fetched when routes are visited)
const SuperAdmin = lazy(() => import('./components/superadmin/SuperAdmin'));
const SuperAdminCustomers = lazy(() => import('./components/superadmin/SuperAdminCustomers'));
const SuperAdminStaff = lazy(() => import('./components/superadmin/SuperAdminStaff'));
const SuperAdminBilling = lazy(() => import('./components/superadmin/SuperAdminBillingEnhanced'));
const SuperAdminMetrics = lazy(() => import('./components/superadmin/SuperAdminMetrics'));
const SuperAdminLiveChat = lazy(() => import('./components/superadmin/SuperAdminLiveChat'));
const SuperAdminTickets = lazy(() => import('./components/superadmin/SuperAdminTickets'));
const SuperAdminMarketing = lazy(() => import('./components/superadmin/SuperAdminMarketing'));
const SuperAdminSupport = lazy(() => import('./components/superadmin/SuperAdminSupport'));
const SuperAdminHR = lazy(() => import('./components/superadmin/SuperAdminHR'));
const SuperAdminHRPipeline = lazy(() => import('./components/superadmin/SuperAdminHRPipeline'));
const SuperAdminJobManagement = lazy(() => import('./components/superadmin/SuperAdminJobManagement'));
const SuperAdminTeamMembers = lazy(() => import('./components/superadmin/SuperAdminTeamMembers'));
const SuperAdminSettings = lazy(() => import('./components/superadmin/SuperAdminSettings'));
const SuperAdminPricingPlans = lazy(() => import('./components/superadmin/SuperAdminPricingPlans'));
const SuperAdminEmailWhitelist = lazy(() => import('./components/superadmin/SuperAdminEmailWhitelist'));

const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./components/auth/VerifyEmail'));
const AcceptInvite = lazy(() => import('./components/auth/AcceptInvite'));
const SurveyPage = lazy(() => import('./components/surveys/SurveyPage'));
const Refer = lazy(() => import('./components/auth/Refer'));
const OnboardingWizard = lazy(() => import('./components/dashboard/OnboardingWizard'));
import { Toaster } from 'sonner';
import { ConfirmDialogProvider } from './components/ui/confirm-dialog';
import { ThemeProvider } from './context/ThemeContext';
import CurrencyContext from './context/CurrencyContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './config/queryClient';
import {
  WithUser,
  WithPublicAuth,
  GuestGuard,
  SuperAdminGuard,
  AdminOnly,
} from './components/auth/RouteGuards';
import { ROUTE_ACCESS } from './constants/featureAccess';
import { useFeatureTracking } from './hooks/useFeatureTracking';
import { ConsentProvider, useConsent } from './context/ConsentContext';
import { useAnalyticsConsentBridge } from './hooks/useAnalyticsConsentBridge';
import ConsentBanner from './components/consent/ConsentBanner';
// Route loaders (routeLoaders.js) are available but not wired yet —
// components currently use raw api.get() instead of useApiQuery,
// so loaders would cause duplicate API requests. Wire them in
// once components are migrated to useApiQuery.
// import { accountsLoader, accountDetailLoader, analyticsLoader, tasksLoader } from './config/routeLoaders';
import './App.css';

// ── Lazy-load Suspense fallback ────────────────────────────────────
const LazyFallback = (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Spinner size="md" color="border-primary" />
  </div>
);

// ── Route-level error fallback (used by React Router errorElement) ─
/**
 * Catches errors thrown from route loaders or components and displays
 * a friendly UI instead of React Router's default "Unexpected Error" page.
 */
const RouteErrorFallback = () => {
  const error = useRouteError();
  const isNotFound = error?.response?.status === 404 || error?.status === 404;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8 text-center">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {isNotFound ? 'Page Not Found' : 'Something went wrong'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {isNotFound
            ? "The page you're looking for doesn't exist."
            : 'An unexpected error occurred. Please try again.'}
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
          <Button onClick={() => (window.location.href = '/app/dashboard')}>
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Root layout rendered for every route ───────────────────────────
/**
 * Root layout wraps all routes. Provides:
 * - Auth-expired handler (navigates to /login on 401)
 * - Onboarding wizard (admin only)
 * - Suspense boundary for lazy-loaded routes
 */
const RootLayout = () => {
  const { user, isAuthenticated, logout, verifyAuth } = useAuth();
  const { consent } = useConsent();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Initializes/identifies PostHog once analytics consent is granted (and
  // handles the already-authenticated-then-consents-later case); disables it
  // again if consent is withdrawn.
  useAnalyticsConsentBridge();

  // Emit `feature_used` on navigation into an app section (once authenticated
  // AND analytics consent is granted — feature-usage events are analytics,
  // so they must not fire before consent either).
  useFeatureTracking(isAuthenticated && consent.analytics);

  // Listen for auth:expired custom events from the 401 interceptor
  useEffect(() => {
    const handler = () => {
      logout();
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [navigate, logout]);

  // Check onboarding status for admin users
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      api.get('/auth/onboarding/status')
        .then((res) => {
          if (res.data.should_show_wizard) setShowOnboarding(true);
        })
        .catch(() => {});
    }
  }, [isAuthenticated, user?.role]);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    await verifyAuth();
  };

  const handleOnboardingSkip = async () => {
    setShowOnboarding(false);
    try {
      await api.post('/auth/onboarding/skip');
      await verifyAuth();
    } catch {
      // ignore
    }
  };

  return (
    <ErrorBoundary>
      <div className="App min-h-screen bg-background text-foreground">
        {showOnboarding && isAuthenticated && user && (
          <Suspense fallback={null}>
            <OnboardingWizard
              user={user}
              onComplete={handleOnboardingComplete}
              onSkip={handleOnboardingSkip}
            />
          </Suspense>
        )}
        <Suspense fallback={LazyFallback}>
          <Outlet />
        </Suspense>
      </div>
      <Toaster />
      <ConfirmDialogProvider />
      {/* Mounted once here so the consent banner/preferences dialog show on every route. */}
      <ConsentBanner />
    </ErrorBoundary>
  );
};

// ── Wrapper for login-related pages ───────────────────────────────
/**
 * Receives onLogin-style components (SignIn, AcceptInvite, SignUp).
 * Bridges the existing onLogin(token, userData) callback to AuthContext.
 */
const LoginWrapper = ({ Component }) => {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (token, userData) => {
    // The session-establishment funnel (persist + identify + `login` event +
    // verify) lives in AuthContext; LoginWrapper only handles post-login routing.
    await completeLogin(token, userData);
    // Super-admin redirect
    if (userData?.internal_role && userData.internal_role !== 'none') {
      navigate('/super-admin', { replace: true });
    } else {
      navigate('/app/dashboard', { replace: true });
    }
  };

  return <Component onLogin={handleLogin} />;
};

// ── Auth-aware index redirect for / and /app ──────────────────────
const AppIndexRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return LazyFallback;
  return isAuthenticated
    ? <Navigate to="/app/dashboard" replace />
    : <Navigate to="/login" replace />;
};

// ── Auth-protected route wrapper for standalone pages ─────────────
const ProtectedPage = ({ Component, ...props }) => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  if (loading) return LazyFallback;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Component user={user} onLogout={logout} {...props} />;
};

// ── Router definition (static, created once at module level) ──────
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/', element: <AppIndexRedirect /> },

      // ─── Public Marketing Pages ─────────────────────────────
      { path: '/features', element: <WithPublicAuth Component={Features} /> },
      { path: '/pricing', element: <WithPublicAuth Component={Pricing} /> },
      { path: '/about', element: <WithPublicAuth Component={About} /> },
      { path: '/contact', element: <WithPublicAuth Component={Contact} /> },
      { path: '/integrations', element: <WithPublicAuth Component={Integrations} /> },
      { path: '/careers', element: <WithPublicAuth Component={Careers} /> },
      { path: '/privacy-policy', element: <WithPublicAuth Component={PrivacyPolicy} /> },
      { path: '/terms-of-service', element: <WithPublicAuth Component={TermsOfService} /> },
      { path: '/cookie-policy', element: <WithPublicAuth Component={CookiePolicy} /> },

      // ─── Public Survey Page ─────────────────────────────────
      { path: '/survey/:surveyId', element: <SurveyPage /> },

      // ─── Super Admin Routes ─────────────────────────────────
      {
        element: <SuperAdminGuard />,
        children: [
          { path: '/super-admin', element: <SuperAdmin /> },
          { path: '/super-admin/customers', element: <SuperAdminCustomers /> },
          { path: '/super-admin/users', element: <SuperAdminStaff /> },
          { path: '/super-admin/billing-control', element: <SuperAdminBilling /> },
          { path: '/super-admin/pricing-plans', element: <SuperAdminPricingPlans /> },
          { path: '/super-admin/email-whitelist', element: <SuperAdminEmailWhitelist /> },
          { path: '/super-admin/metrics', element: <SuperAdminMetrics /> },
          { path: '/super-admin/live-chat', element: <SuperAdminLiveChat /> },
          { path: '/super-admin/tickets', element: <SuperAdminTickets /> },
          { path: '/super-admin/marketing', element: <SuperAdminMarketing /> },
          { path: '/super-admin/support', element: <SuperAdminSupport /> },
          { path: '/super-admin/hr', element: <SuperAdminHR /> },
          { path: '/super-admin/hr/pipeline', element: <SuperAdminHRPipeline /> },
          { path: '/super-admin/hr/jobs', element: <SuperAdminJobManagement /> },
          { path: '/super-admin/team-members', element: <SuperAdminTeamMembers /> },
          { path: '/super-admin/settings', element: <SuperAdminSettings /> },
        ],
      },

      // ─── Authentication Routes ──────────────────────────────
      {
        path: '/login',
        element: <GuestGuard><LoginWrapper Component={SignIn} /></GuestGuard>,
      },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/verify-email', element: <VerifyEmail /> },
      {
        path: '/accept-invite',
        element: <GuestGuard><LoginWrapper Component={AcceptInvite} /></GuestGuard>,
      },
      {
        path: '/sign-up',
        element: <GuestGuard><LoginWrapper Component={SignUp} /></GuestGuard>,
      },

      // ─── Protected App Routes ───────────────────────────────
      { path: '/app', element: <AppIndexRedirect /> },
      {
        element: <ProtectedLayout featureName="App" />,
        children: [
          { path: '/app/dashboard', element: <WithUser Component={Accounts} anyOf={ROUTE_ACCESS['/app/dashboard']} /> },
          { path: '/app/accounts', element: <WithUser Component={Accounts} anyOf={ROUTE_ACCESS['/app/accounts']} /> },
          { path: '/app/accounts/:accountId', element: <WithUser Component={AccountDetail} anyOf={ROUTE_ACCESS['/app/accounts']} /> },
          { path: '/app/communications', element: <WithUser Component={Communications} anyOf={ROUTE_ACCESS['/app/communications']} /> },
          { path: '/app/ai-actions', element: <WithUser Component={AIActions} anyOf={ROUTE_ACCESS['/app/ai-actions']} /> },
          { path: '/app/onboarding', element: <WithUser Component={Onboarding} anyOf={ROUTE_ACCESS['/app/onboarding']} /> },
          { path: '/app/onboarding/insights', element: <WithUser Component={OnboardingInsights} anyOf={ROUTE_ACCESS['/app/onboarding']} /> },
          { path: '/app/tasks-board', element: <WithUser Component={TasksPage} anyOf={ROUTE_ACCESS['/app/tasks-board']} /> },
          { path: '/app/meetings', element: <WithUser Component={MeetingsPage} anyOf={ROUTE_ACCESS['/app/meetings']} /> },
          { path: '/app/opportunities', element: <WithUser Component={OpportunitiesPage} anyOf={ROUTE_ACCESS['/app/opportunities']} /> },
          { path: '/app/settings', element: <WithUser Component={Settings} /> },
          { path: '/app/business-profile', element: <AdminOnly Component={BusinessProfile} /> },
          { path: '/app/analytics', element: <WithUser Component={Analytics} anyOf={ROUTE_ACCESS['/app/analytics']} /> },
          { path: '/app/chat', element: <WithUser Component={CalebChat} anyOf={ROUTE_ACCESS['/app/chat']} /> },
          { path: '/app/chat/:conversationId', element: <WithUser Component={CalebChat} anyOf={ROUTE_ACCESS['/app/chat']} /> },
          { path: '/app/refer', element: <WithUser Component={Refer} /> },
          { path: '/app/knowledge-base', element: <WithUser Component={KnowledgeBase} anyOf={ROUTE_ACCESS['/app/knowledge-base']} /> },
        ],
      },

      // ─── MyIntegrations (has its own nav) ───────────────────
      {
        path: '/app/my-integrations',
        element: <ProtectedPage Component={MyIntegrations} />,
      },

      // ─── API Docs (authenticated only) ──────────────────────
      { path: '/api-docs', element: <ProtectedPage Component={ApiDocs} /> },

    ],
  },
]);

/** Reads user from AuthContext to set the CurrencyContext value. */
const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  return (
    <CurrencyContext.Provider value={user?.currency || 'USD'}>
      {children}
    </CurrencyContext.Provider>
  );
};

// ── Main App component ────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider>
      <ConsentProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CurrencyProvider>
              <RouterProvider router={router} />
            </CurrencyProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ConsentProvider>
    </ThemeProvider>
  );
}

export default App;